/**
 * AdminDuplicateClustersPage.js — /admin/duplicates
 *
 * Read-only supervisory view of potential duplicate role clusters
 * across the whole platform. Feeds off the admin-only endpoint
 * `GET /api/admin/duplicate-clusters`, which aggregates the same
 * `core.role_dedupe.score_candidate` logic used by the employer
 * review modal.
 *
 * Rules (see system_state/RC_1.30_before_admin_duplicate_view.md):
 *   • No mutation. No merge. No delete. No auto-link.
 *   • Only role-level data is surfaced. No candidate data, no
 *     signals, no PII.
 *   • UI language is supervisory, never actionable.
 *
 * Allowed actions:
 *   • Open a cluster detail modal (side-by-side comparison).
 *   • Navigate to a role editor (existing route — read via
 *     the same employer gate).
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertCircle, Copy, Info, Loader2, Shield,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';

/* ────────────────────────────────────────────────────────────
 * Helpers
 * ────────────────────────────────────────────────────────── */

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

function StatusPill({ status }) {
  const cls =
    status === 'live'
      ? 'state-pill state-pill--accepted'
      : status === 'draft'
        ? 'state-pill state-pill--draft'
        : 'state-pill state-pill--muted';
  return <span className={cls}>{status}</span>;
}

/* ────────────────────────────────────────────────────────────
 * Skeleton
 * ────────────────────────────────────────────────────────── */

function ClusterSkeleton() {
  return (
    <Card className="border-ndor-edge shadow-sm animate-pulse">
      <CardContent className="p-5 space-y-4">
        <div className="h-4 w-48 rounded bg-ndor-surface-alt" />
        <div className="h-3 w-3/4 rounded bg-ndor-surface-alt" />
        <div className="space-y-2">
          <div className="h-3 w-full rounded bg-ndor-surface-alt" />
          <div className="h-3 w-5/6 rounded bg-ndor-surface-alt" />
          <div className="h-3 w-2/3 rounded bg-ndor-surface-alt" />
        </div>
      </CardContent>
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────
 * Cluster card
 * ────────────────────────────────────────────────────────── */

function ClusterCard({ cluster, onOpen }) {
  return (
    <Card
      className="border-ndor-edge shadow-sm state-rail--pending"
      data-testid={`duplicate-cluster-${cluster.cluster_id}`}
    >
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Copy className="h-4 w-4 text-ndor-ink-muted" />
            <div
              className="text-sm font-medium text-ndor-ink font-mono-ui"
              data-testid={`duplicate-cluster-id-${cluster.cluster_id}`}
            >
              Cluster #{cluster.cluster_id.slice(0, 8)}
            </div>
            <span
              className="state-pill state-pill--pending"
              data-testid={`duplicate-cluster-size-${cluster.cluster_id}`}
            >
              {cluster.cluster_size} roles
            </span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onOpen(cluster)}
            data-testid={`duplicate-cluster-details-${cluster.cluster_id}`}
          >
            View details →
          </Button>
        </div>

        {cluster.similarity_reasons?.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-ndor-ink-muted mb-1.5">
              Why flagged
            </div>
            <ul
              className="space-y-0.5 text-xs text-ndor-ink-secondary"
              data-testid={`duplicate-cluster-reasons-${cluster.cluster_id}`}
            >
              {cluster.similarity_reasons.slice(0, 4).map((r, i) => (
                <li key={i} className="leading-relaxed">• {r}</li>
              ))}
              {cluster.similarity_reasons.length > 4 && (
                <li className="text-ndor-ink-muted italic">
                  + {cluster.similarity_reasons.length - 4} more
                </li>
              )}
            </ul>
          </div>
        )}

        <div>
          <div className="text-[10px] uppercase tracking-wider text-ndor-ink-muted mb-2">
            Roles in this cluster
          </div>
          <div className="rounded-md border border-ndor-edge overflow-hidden">
            {cluster.roles.map((r, i) => (
              <div
                key={r.id}
                className={`flex items-center gap-3 px-3 py-2 text-xs ${
                  i > 0 ? 'border-t border-ndor-edge' : ''
                }`}
                data-testid={`duplicate-cluster-role-${r.id}`}
              >
                <StatusPill status={r.status} />
                <div className="flex-1 min-w-0 truncate text-ndor-ink font-medium">
                  {r.title || '(untitled)'}
                </div>
                <div className="text-ndor-ink-secondary truncate w-[180px] shrink-0">
                  {r.organisation_name || '—'}
                </div>
                <div className="text-ndor-ink-muted shrink-0 w-[80px] text-right">
                  {formatDate(r.created_at)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────
 * Detail modal — side-by-side
 * ────────────────────────────────────────────────────────── */

function ClusterDetailModal({ cluster, open, onOpenChange }) {
  if (!cluster) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl"
        data-testid="duplicate-cluster-detail-modal"
      >
        <DialogHeader>
          <DialogTitle>Duplicate cluster</DialogTitle>
          <DialogDescription>
            Read-only supervisory view of {cluster.cluster_size}{' '}
            potentially-similar roles. No action can be taken from
            this view — open a role to review it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div
            className={`grid gap-3 ${
              cluster.roles.length === 2
                ? 'grid-cols-2'
                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            }`}
            data-testid="duplicate-cluster-grid"
          >
            {cluster.roles.map((r) => (
              <div
                key={r.id}
                className="rounded-md border border-ndor-edge p-3 space-y-2 text-xs bg-ndor-surface-alt/40"
                data-testid={`duplicate-cluster-role-detail-${r.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <StatusPill status={r.status} />
                  <Link
                    to={`/employer/roles/${r.id}`}
                    className="text-ndor-ink-secondary hover:text-ndor-ink underline decoration-dotted underline-offset-2"
                    data-testid={`duplicate-cluster-role-link-${r.id}`}
                  >
                    Open role →
                  </Link>
                </div>
                <div className="text-sm font-semibold text-ndor-ink leading-tight">
                  {r.title || '(untitled)'}
                </div>
                <div className="space-y-1.5 pt-1 border-t border-ndor-edge">
                  <KV label="Organisation" value={r.organisation_name} />
                  <KV label="Domain"       value={r.domain} />
                  <KV label="Created"      value={formatDate(r.created_at)} />
                  <KV label="Role ID"
                      value={<span className="font-mono-ui text-[10px]">{r.id}</span>} />
                </div>
              </div>
            ))}
          </div>

          {cluster.similarity_reasons?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-ndor-ink-muted mb-2">
                Why these were flagged as similar
              </div>
              <ul
                className="space-y-1 text-xs text-ndor-ink-secondary"
                data-testid="duplicate-cluster-detail-reasons"
              >
                {cluster.similarity_reasons.map((r, i) => (
                  <li key={i} className="leading-relaxed">• {r}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function KV({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-[10px] uppercase tracking-wider text-ndor-ink-muted w-24 shrink-0">
        {label}
      </span>
      <span className="text-ndor-ink flex-1 min-w-0 break-words text-right">
        {value || '—'}
      </span>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
 * Page
 * ────────────────────────────────────────────────────────── */

export default function AdminDuplicateClustersPage() {
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [payload, setPayload] = useState(null);
  const [active,  setActive]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await apiFetch('/api/admin/duplicate-clusters');
      setPayload(d);
    } catch (err) {
      setError(err?.data?.detail || err?.message || 'Failed to load clusters.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const clusters = payload?.clusters || [];

  return (
    <div className="max-w-5xl mx-auto space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-ndor-ink flex items-center gap-2">
          <Shield className="h-5 w-5 text-ndor-ink-muted" />
          Duplicate clusters
        </h1>
        <p className="mt-1 text-sm text-ndor-ink-muted">
          Potential duplicate roles across organisations. Review only.
        </p>
      </div>

      {!loading && !error && payload && (
        <div
          className="rounded-md border border-ndor-edge bg-ndor-surface-alt px-3 py-2 text-[11px] text-ndor-ink-secondary leading-relaxed flex items-start gap-2"
          data-testid="duplicate-clusters-summary"
        >
          <Info className="h-3.5 w-3.5 mt-0.5 text-ndor-ink-muted shrink-0" />
          <span>
            Scanned {payload.total_roles_scanned} role
            {payload.total_roles_scanned === 1 ? '' : 's'}. Found{' '}
            {payload.total_clusters} cluster
            {payload.total_clusters === 1 ? '' : 's'}. Clusters show
            transitively-similar roles using the same scoring logic
            surfaced to employers.
          </span>
        </div>
      )}

      {loading && (
        <div className="space-y-3" data-testid="duplicate-clusters-loading">
          <ClusterSkeleton />
          <ClusterSkeleton />
          <ClusterSkeleton />
        </div>
      )}

      {!loading && error && (
        <Card
          className="border-rose-500/40 bg-rose-500/10 shadow-none"
          data-testid="duplicate-clusters-error"
        >
          <CardContent className="py-4 flex items-start gap-2 text-sm text-rose-900">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">Could not load clusters</p>
              <p className="text-xs text-rose-200 mt-1">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={load}
                className="mt-3"
                data-testid="duplicate-clusters-retry"
              >
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && !error && clusters.length === 0 && (
        <Card
          className="border-ndor-edge shadow-sm"
          data-testid="duplicate-clusters-empty"
        >
          <CardContent className="py-14 text-center">
            <Shield className="mx-auto mb-3 h-8 w-8 text-ndor-ink-muted" />
            <p className="text-sm font-medium text-ndor-ink">
              No duplicate clusters
            </p>
            <p className="text-xs text-ndor-ink-muted mt-1 max-w-md mx-auto">
              No overlapping roles detected.
            </p>
          </CardContent>
        </Card>
      )}

      {!loading && !error && clusters.length > 0 && (
        <div className="space-y-3" data-testid="duplicate-clusters-list">
          {clusters.map((c) => (
            <ClusterCard
              key={c.cluster_id}
              cluster={c}
              onOpen={setActive}
            />
          ))}
        </div>
      )}

      <ClusterDetailModal
        cluster={active}
        open={!!active}
        onOpenChange={(v) => { if (!v) setActive(null); }}
      />
    </div>
  );
}
